import { useEffect, useState } from "react"
import { Table } from "reactstrap"
import ActionButton from "Components/atoms/buttons/action-button"
import LoadingState from "Components/organisms/loading-state"
import MonthlyCostCodeRows from "./monthly-cost-code-rows"
import { ExpenseBudget, ExpenseBudgetLine, PlanVsActual, PlanView } from "models/expense-budget"
import { getExpenseBudgetPlanVsActual } from "slices/finance/expense-budget/thunk"
import { budgetThreshold } from "utils/expense-budget"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"

interface MonthlyTabProps {
    budget: ExpenseBudget
    lines: ExpenseBudgetLine[]
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const VIEW_HELP: Record<PlanView, string> = {
    [PlanView.Cash]: "Cash: each bill in the month it arrives (renewals hit in full).",
    [PlanView.Accounting]: "Accounting: prepaid lines spread ÷ 12, the way GL actuals are booked.",
}

// Read-only plan vs actual by month. Amounts come from the Budget lines; change them there.
const MonthlyTab = ({ budget, lines }: MonthlyTabProps) => {
    const [view, setView] = useState<PlanView>(PlanView.Accounting)
    const [planVsActual, setPlanVsActual] = useState<PlanVsActual | null>(null)

    useEffect(() => {
        getExpenseBudgetPlanVsActual(budget.id, view).then(setPlanVsActual)
    }, [budget, view, lines])

    if (!planVsActual) return <LoadingState title="Loading Monthly Plan" subtitle="Comparing plan and actuals..." />

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-wrap gap-2 align-items-center">
                {[PlanView.Accounting, PlanView.Cash].map(option => (
                    <ActionButton key={option} className={option === view ? "active" : ""} onClick={() => setView(option)}>
                        {option === PlanView.Cash ? "Cash view" : "Accounting view"}
                    </ActionButton>
                ))}
                <span className="text-muted">{VIEW_HELP[view]}</span>
            </div>
            <div style={{ overflowX: "auto" }}>
                <Table size="sm" bordered className="align-middle">
                    <thead>
                        <tr>
                            <th style={{ position: "sticky", left: 0, background: "var(--bs-body-bg)" }}>Cost code / Line</th>
                            {planVsActual.periods.map(period => (
                                <th key={period} className="text-end">{calendar.periodToMonth(period % 100, "en-CA", true)}</th>
                            ))}
                            <th className="text-end">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {planVsActual.costCodes.map(costCode => (
                            <MonthlyCostCodeRows key={costCode.id} costCode={costCode} periods={planVsActual.periods}
                                firstOpenPeriod={planVsActual.firstOpenPeriod} threshold={budgetThreshold(budget)} />
                        ))}
                    </tbody>
                </Table>
            </div>
            <span className="text-muted">
                Actuals are GL postings on each cost code's account and project, in {budget.department.description} and the departments under it.
                Months still within the closing lag show "—".
            </span>
        </div>
    )
}

export default MonthlyTab
